<template xmlns:v-slot="http://www.w3.org/1999/XSL/Transform">
  <div class="employees-list">
    <foldable-card
      v-if="hasEmSub"
      non-foldable
      title="Employee Compliance Overview"
      description="Select an employee below to view, edit, or terminate."
    >
      <v-row class="ma-0 pa-0">
        <v-col class="ma-0 pa-0">
          <v-text-field
            outlined
            dense
            class="ma-0 pa-0"
            prepend-inner-icon="fa-search"
            placeholder="Search by first name, last name, or date of birth..."
            v-model.trim="searchText"
            @keyup.enter="search"
          />
        </v-col>
        <v-col class="ma-0 pt-0 pr-0 pb-0">
          <v-btn class="pt-5 pb-5 search-button" @click="search">
            Search
          </v-btn>
        </v-col>
      </v-row>

      <v-data-table
        class="main-table"
        :headers="headers"
        :items="items"
        :server-items-length="total"
        :options.sync="searchOptions"
        :footer-props="{
          itemsPerPageOptions: [10, 25, 50],
          showFirstLastPage: true,
          showCurrentPage: true
        }"
      >
        <!-- Note, we need items slot for row level class until https://github.com/vuetifyjs/vuetify/issues/8643 is implemented -->
        <template v-slot:item="{ item }">
          <tr :class="getAlertClass(item.status.name)" :key="item.employeeId">
            <td>
              <span
                class="dot mr-1"
                :class="getStatusColor(item.status.name)"
              ></span>
              {{ item.status.desc }}
            </td>
            <td>{{ item.firstName }}</td>
            <td>{{ item.lastName }}</td>
            <td>{{ item.dob }}</td>
            <td class="text-right">
              <v-btn
                depressed
                height="32px"
                width="32px"
                class="tertiary mr-4"
                title="Terminate Employee"
                @click="initiateTermination(item)"
              >
                <v-icon class="terminate-button-icon">$boldx</v-icon>
              </v-btn>
              <v-btn
                height="32px"
                min-width="32px"
                :to="'/employees/' + item.employeeId + '/detail'"
                class="tertiary"
                depressed
              >
                <v-icon color="primary">mdi-arrow-right</v-icon>
              </v-btn>
            </td>
          </tr>
        </template>
      </v-data-table>
    </foldable-card>
    <employee-monitoring-pitch
      location="Employees Page"
      v-else-if="!loadingEmployees"
    ></employee-monitoring-pitch>

    <v-dialog v-model="controls.deleteConfirmationDialogOpen" width="672">
      <v-card class="pa-4">
        <v-card-title class="justify-center pt-0">
          <v-icon size="48" color="colorAlertRed">mdi-alert-rhombus</v-icon>
          <v-btn icon absolute top right @click="cancelTermination">
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          Are you sure you want to remove this employee? They will no longer be
          visible and monitoring will no longer be active for this employee.
        </v-card-text>

        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn text @click="cancelTermination">
            Cancel
          </v-btn>

          <v-btn color="primary" @click="actuallyTerminate">
            Terminate Employee
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import FoldableCard from "@/components/FoldableCard";
import { employeesAPI, employeeByIdAPI } from "@/services/api";
import { get, _delete } from "@/services/request";
import hasStatusFactory from "@/mixins/has-status-factory";
import EmployeeMonitoringPitch from "@/components/EmployeeMonitoringPitch";
import hasNotifications from "@/mixins/has-notifications";
import { getCurrentUser } from "@/services/shared";

const statusColorMap = {
  NON_COMPLIANT: "error",
  COMPLIANT: "success"
};

export default {
  name: "Employees",
  mixins: [hasStatusFactory(statusColorMap), hasNotifications],
  components: {
    FoldableCard,
    EmployeeMonitoringPitch
  },
  data: () => ({
    items: [],
    employeeToDelete: null,
    headers: [
      {
        text: "Compliance Status",
        value: "status"
      },
      { text: "First Name", value: "firstName" },
      { text: "Last Name", value: "lastName" },
      { text: "Date of Birth", value: "dob" },
      { text: " ", value: "action", align: "end", sortable: false }
    ],
    searchText: "",
    searchOptions: {
      filter: "",
      page: 1,
      itemsPerPage: 25,
      sortBy: [],
      sortDesc: []
    },
    total: 0,
    pageCount: 0,
    loadingEmployees: true,
    controls: {
      deleteConfirmationDialogOpen: false
    }
  }),
  created() {
    this.loadData();
  },
  methods: {
    loadData() {
      get(employeesAPI, {
        page: this.searchOptions.page - 1, //vuetify uses 1-indexed page, server uses 0-indexed
        size: this.searchOptions.itemsPerPage,
        sortBy: this.searchOptions.sortBy.at(0),
        sortDesc: this.searchOptions.sortDesc.at(0),
        filter: this.searchOptions.filter
      })
        .then(response => {
          if (response && response.data) {
            this.items = this.items = response.data.content;
            this.total =
              "total" in response.data ? response.data.total : this.total;
            this.pageCount = Math.ceil(
              this.total / this.searchOptions.itemsPerPage
            );
          }
        })
        .finally(() => (this.loadingEmployees = false));
    },
    actuallyTerminate() {
      this.controls.deleteConfirmationDialogOpen = false;
      let employee = this.employeeToDelete;
      this.employeeToDelete = null;
      _delete(employeeByIdAPI.getUrl(employee.employeeId)).then(() => {
        this.items = this.items.filter(it => it !== employee);
        this.showMessage(
          `${employee.firstName} ${employee.lastName} was successfully terminated`
        );
      });
    },
    initiateTermination(employeeId) {
      this.controls.deleteConfirmationDialogOpen = true;
      this.employeeToDelete = employeeId;
    },
    cancelTermination() {
      this.controls.deleteConfirmationDialogOpen = false;
      this.employeeToDelete = null;
    },
    search() {
      this.searchOptions.filter = this.searchText;
      this.searchOptions.page = 1;
      this.loadData();
    }
  },
  computed: {
    hasEmSub() {
      return getCurrentUser().hasEmployeeSubscription;
    }
  },
  watch: {
    searchOptions: {
      handler(newValue, oldValue) {
        //Ensure data we care about has changed before loading data.
        //(This prevents loadData from being called twice on initial page load,
        //which is happening regardless of loadData being called from created or mounted.
        //The searchOptions object will get other properties set into it that we aren't using (groupBy, multiSort, etc.),
        //which may be confusing vue.)
        if (
          newValue.page !== oldValue.page ||
          newValue.itemsPerPage !== oldValue.itemsPerPage ||
          newValue.sortBy[0] !== oldValue.sortBy[0] ||
          newValue.sortDesc[0] !== oldValue.sortDesc[0] ||
          newValue.filter !== oldValue.filter
        ) {
          this.loadData();
        }
      },
      deep: true
    }
  }
};
</script>

<style lang="sass" scoped>
.v-btn
  font-size: 1rem

.tertiary.v-btn:not(.v-btn--round).v-size--default
  padding: 0
  min-width: 0

.search-button
  background-color: $primary !important
  color: white
</style>
